import {
  SESSION_SCOPED_STORAGE_FIELDS,
  SESSION_SCOPED_STORAGE_PREFIXES,
} from '@shared/constants/storage-keys';
import { logger } from './logger';

/** `value` with every property named in `fields` set to '', at any depth. */
function blankFields(value: unknown, fields: readonly string[]): unknown {
  if (Array.isArray(value)) return value.map((item) => blankFields(item, fields));
  if (typeof value !== 'object' || value === null) return value;
  return Object.fromEntries(
    Object.entries(value).map(([name, item]) => [
      name,
      fields.includes(name) ? '' : blankFields(item, fields),
    ]),
  );
}

/** Rewrites one stored entry without `fields`; one that cannot be read goes. */
function blankStoredFields(key: string, fields: readonly string[]): void {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return;
    localStorage.setItem(key, JSON.stringify(blankFields(JSON.parse(raw), fields)));
  } catch {
    // Unreadable (or unwritable): what it holds cannot be checked, so it goes.
    localStorage.removeItem(key);
  }
}

/**
 * Type-safe, fail-soft wrapper around `localStorage`. Every operation is
 * guarded so a disabled/full/unavailable storage (private mode, quota) never
 * throws into application code.
 */
export const storage = {
  get<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch (error) {
      logger.warn(`storage.get failed for "${key}"`, error);
      return null;
    }
  },

  set<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      logger.warn(`storage.set failed for "${key}"`, error);
    }
  },

  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.warn(`storage.remove failed for "${key}"`, error);
    }
  },

  /**
   * Ends what a session left in this browser: removes every entry under
   * SESSION_SCOPED_STORAGE_PREFIXES and blanks the SESSION_SCOPED_STORAGE_FIELDS
   * inside the entries that are kept.
   */
  purgeSessionScoped(): void {
    try {
      const keys = Array.from({ length: localStorage.length }, (_, index) =>
        localStorage.key(index),
      );
      for (const key of keys) {
        if (!key) continue;
        if (SESSION_SCOPED_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
          localStorage.removeItem(key);
          continue;
        }
        const fields = Object.entries(SESSION_SCOPED_STORAGE_FIELDS).find(([prefix]) =>
          key.startsWith(prefix),
        )?.[1];
        if (fields) blankStoredFields(key, fields);
      }
    } catch (error) {
      logger.warn('storage.purgeSessionScoped failed', error);
    }
  },
} as const;
